// Pure logic for the concierge SmsThreadPanel — unit-tested; the React
// component stays thin. Keeps guardian-slot derivation, the concierge
// owner's auto-inclusion, and outbound payload shapes in one place so they
// can be tested without touching axios or the DOM.

// Guardian checkbox options, derived from the backend's
// `GET /concierge/:id/sms/recipients` response — NOT from the legacy
// profile.guardianN_phone columns. The backend resolves guardians primarily
// from the relational AthleteGuardian model (falling back to the legacy
// columns only when no accepted relational guardian exists for a slot), so
// the selector must mirror that same shape or a relational-only guardian
// would show no checkbox and could never be texted. Returns [] when the
// response is missing/malformed or has no textable guardians.
export function guardianCheckboxes(recipientsResponse) {
  const guardians = recipientsResponse?.guardians;
  if (!Array.isArray(guardians)) return [];
  return guardians.map(g => ({ slot: g.slot, name: g.name }));
}

// "Include my cell" defaults ON once the group includes a guardian (staff
// wants to be looped into guardian conversations by default), OFF for a
// 1:1 athlete text.
export function defaultIncludeCell(anyGuardianSelected) {
  return !!anyGuardianSelected;
}

// Shape of the POST body for starting a new thread. The athlete is a
// selectable recipient (`includeAthlete`), no longer force-included by the
// backend, so staff can text a guardian only. `mediaUrl` (an S3 URL returned
// by the sms/upload endpoint) is included only when present, so text-only
// calls keep the original payload shape.
export function buildStartPayload({ includeAthlete, guardianSlots, includeCell, body, mediaUrl }) {
  return {
    include_athlete: !!includeAthlete,
    guardians: guardianSlots,
    include_my_cell: !!includeCell,
    body,
    ...(mediaUrl ? { media_url: mediaUrl } : {}),
  };
}

// Whether a new thread has at least one textable non-staff recipient — the
// athlete (only when they have a phone on file) or any selected guardian.
// A staff-cell-only thread is pointless, so it doesn't count. Mirrors the
// backend's "Select at least one recipient" guard so "Start text" enables
// exactly when the POST would succeed.
export function hasStartRecipient({ includeAthlete, athleteHasPhone, guardianSlots }) {
  return (!!includeAthlete && !!athleteHasPhone) || (Array.isArray(guardianSlots) && guardianSlots.length > 0);
}

// Shape of the POST body for a follow-up message on an existing thread.
export function buildFollowUpPayload({ body, mediaUrl }) {
  return {
    body,
    ...(mediaUrl ? { media_url: mediaUrl } : {}),
  };
}

// The family's concierge owner is in EVERY group (backend-enforced). The
// backend computes the block message so the web copy can never drift from
// the server's refusal text.
export function ownerInfo(recipientsResponse) {
  const o = recipientsResponse?.owner;
  if (!o) return { name: null, textable: false, isMe: false, blockMessage: null };
  return { name: o.name || null, textable: !o.block_message, isMe: !!o.is_me, blockMessage: o.block_message || null };
}

// "Include my cell" is for a covering staff member only — the owner is
// already in the group on their own cell.
export function showIncludeCell(recipientsResponse) {
  return !!recipientsResponse?.staff_has_cell && !recipientsResponse?.owner?.is_me;
}

const ROLE_LABELS = { athlete: 'Athlete', guardian1: 'Guardian 1', guardian2: 'Guardian 2', owner: 'Owner', staff: 'Staff' };

// Named member list for a thread header / list row.
export function rosterLabel(thread) {
  return (thread?.participants || []).map(p => {
    const name = p.name || ROLE_LABELS[p.role] || p.role;
    return p.role === 'owner' ? `${name} (owner)` : name;
  }).join(', ');
}

// The athlete's SMS-capable phone, trimmed. Empty string when absent.
export function athletePhone(profile) {
  return (profile?.cell_phone || '').trim();
}

const FAMILY = (role) => role === 'athlete' || /^guardian/.test(role || '');

// The open thread whose family members are exactly the chosen recipients, so the
// Overview composer continues a conversation instead of starting a duplicate one.
// `threads` arrive newest first, so the first match is the live conversation.
export function pickThreadFor(threads, { includeAthlete, guardianSlots }) {
  const want = [...(includeAthlete ? ['athlete'] : []), ...(guardianSlots || [])].sort().join('|');
  return (threads || []).find(t => t.status !== 'closed'
    && (t.participants || []).map(p => p.role).filter(FAMILY).sort().join('|') === want) || null;
}

export function formatPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '');
  const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return raw || '';
}
